// Bottom sheet drawer for metric detail view
// Slides up from bottom with drag to dismiss
// Supports left/right navigation between metrics

import { useRef, useEffect, useCallback, useState } from 'react'
import { motion, AnimatePresence, useDragControls, PanInfo } from 'framer-motion'
import type { MetricDetail } from '@/types/analysis'
import { getStatusColor, getStatusLabel } from '@/types/analysis'
import { METRIC_INFO } from '@/data/metricExplanations'
import { HeatmapViewer } from './HeatmapViewer'
import { ScoreComparisonBar } from './ScoreComparisonBar'
import { ZoneBreakdown } from './ZoneBreakdown'

interface MetricDetailSheetProps {
  isOpen: boolean
  onClose: () => void
  metric: MetricDetail | null
  photoUrl: string
  // Navigation props
  allMetrics?: MetricDetail[]
  onNavigate?: (metric: MetricDetail) => void
}

export function MetricDetailSheet({ isOpen, onClose, metric, photoUrl, allMetrics, onNavigate }: MetricDetailSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const dragControls = useDragControls()
  const [direction, setDirection] = useState<'left' | 'right'>('right')

  // Find current index and navigation helpers
  const currentIndex = metric && allMetrics ? allMetrics.findIndex(m => m.key === metric.key) : -1
  const hasPrev = currentIndex > 0
  const hasNext = currentIndex >= 0 && currentIndex < (allMetrics?.length || 0) - 1

  const goToPrev = useCallback(() => {
    if (hasPrev && allMetrics && onNavigate) {
      setDirection('left')
      onNavigate(allMetrics[currentIndex - 1])
    }
  }, [hasPrev, allMetrics, currentIndex, onNavigate])

  const goToNext = useCallback(() => {
    if (hasNext && allMetrics && onNavigate) {
      setDirection('right')
      onNavigate(allMetrics[currentIndex + 1])
    }
  }, [hasNext, allMetrics, currentIndex, onNavigate])

  // Scroll to top when metric changes
  useEffect(() => {
    if (contentRef.current && metric) {
      contentRef.current.scrollTop = 0
    }
  }, [metric])

  // Handle keyboard navigation (escape, arrows)
  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') goToPrev()
      if (e.key === 'ArrowRight') goToNext()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleKeydown)
      // Prevent body scroll when sheet is open
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleKeydown)
      document.body.style.overflow = ''
    }
  }, [isOpen, onClose, goToPrev, goToNext])

  if (!metric) return null

  const info = METRIC_INFO[metric.key]
  const statusColor = getStatusColor(metric.status)

  const handleDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    // Close if dragged down more than 100px or with high velocity
    if (info.offset.y > 100 || info.velocity.y > 500) {
      onClose()
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Sheet */}
          <motion.div
            ref={sheetRef}
            className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl max-h-[100vh] overflow-hidden"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            drag="y"
            dragControls={dragControls}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.5 }}
            onDragEnd={handleDragEnd}
          >
            {/* Drag handle */}
            <div
              className="flex justify-center py-3 cursor-grab active:cursor-grabbing"
              onPointerDown={(e) => dragControls.start(e)}
            >
              <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-5 pb-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{info.icon}</span>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{metric.name}</h2>
                  <p className="text-sm" style={{ color: statusColor }}>
                    {getStatusLabel(metric.status)}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Navigation buttons */}
            {allMetrics && allMetrics.length > 1 && (
              <>
                {/* Left (Previous) button */}
                <button
                  onClick={goToPrev}
                  disabled={!hasPrev}
                  className={`absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all ${
                    hasPrev
                      ? 'bg-white/90 hover:bg-white text-gray-800'
                      : 'bg-gray-200/50 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>

                {/* Right (Next) button */}
                <button
                  onClick={goToNext}
                  disabled={!hasNext}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all ${
                    hasNext
                      ? 'bg-white/90 hover:bg-white text-gray-800'
                      : 'bg-gray-200/50 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>

                {/* Navigation indicator */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 bg-black/60 backdrop-blur-sm rounded-full px-3 py-1">
                  <span className="text-white text-xs font-medium">
                    {currentIndex + 1} / {allMetrics.length}
                  </span>
                </div>
              </>
            )}

            {/* Scrollable content */}
            <div ref={contentRef} className="overflow-y-auto max-h-[calc(100vh-100px)] pb-safe overflow-x-hidden">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={metric.key}
                  initial={{ x: direction === 'right' ? 100 : -100, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: direction === 'right' ? -100 : 100, opacity: 0 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                  className="p-5 space-y-6"
                >
                  {/* Heatmap */}
                  <HeatmapViewer
                    photoUrl={photoUrl}
                    heatmapUrl={metric.heatmapUrl}
                    metricName={metric.name}
                    showControls={true}
                  />

                  {/* Score comparison */}
                  <div className="bg-gray-50 rounded-2xl p-4">
                    <ScoreComparisonBar
                      userScore={metric.score}
                      ageAverage={metric.ageAverage}
                      status={metric.status}
                      showLabels={true}
                      animated={true}
                    />
                  </div>

                  {/* Zone breakdown (if applicable) */}
                  {metric.zones && Object.keys(metric.zones).length > 0 && (
                    <div className="bg-gray-50 rounded-2xl p-4">
                      <ZoneBreakdown
                        metricKey={metric.key}
                        zones={metric.zones}
                        animated={true}
                      />
                    </div>
                  )}

                  {/* What this means */}
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 mb-2">What This Means</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{info.explanation}</p>
                  </div>

                  {/* Recommended ingredients */}
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 mb-3">Recommended Ingredients</h3>
                    <div className="space-y-2">
                      {info.ingredients.map((ingredient, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <svg
                            className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          <span className="text-sm text-gray-700">{ingredient}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
